// the loop's Phase 3 screens: Getting Around (511 arrivals at Jared's stops), Fog Watch (GOES-18 GeoColor over the
// Bay), Air & Fires (always), This Day in SF Weather (ACIS records) and Recent Earthquakes (after a local quake)
import { registerDisplay } from '../navigation.mjs';
import { escapeHtml } from '../utils/string.mjs';
import SnapshotDisplay from './snapshot-display.mjs';
import FeatureDisplay from './feature-display.mjs';
import { data, fetchedAt } from './snapshot.mjs';
import {
	asOf, time, weekday, isToday, dayKey,
} from './format.mjs';

const { stops } = window.RHL;
const HOUR = 3600000;
const kv = (rows) => `<div class="kv">${rows.filter(Boolean).map(([k, v]) => `<div>${k}</div><div class="v">${v}</div>`).join('')}</div>`;

// minutes until each arrival, counted from the stored times so they stay right between fetches
const minutesAway = (arrivals, now) => (arrivals ?? [])
	.map((a) => Math.floor((new Date(a.t) - now) / 60000))
	.filter((m) => m >= 0)
	.slice(0, 2);

class GettingAround extends SnapshotDisplay {
	constructor(navId, elemId) {
		super(navId, elemId, 'Getting Around', { seconds: 18 });
	}

	// eslint-disable-next-line class-methods-use-this
	hasData() {
		const now = new Date();
		return !!data('transit')?.some((s) => minutesAway(s.arrivals, now).length);
	}

	// eslint-disable-next-line class-methods-use-this
	render(now) {
		const byStop = Object.fromEntries((data('transit') ?? []).map((s) => [s.stop, s.arrivals]));
		const row = (stop) => {
			const mins = minutesAway(byStop[stop.stop], now);
			// route and direction only; the stops are Jared's own, so the corner goes without saying
			const label = stop.route === 'PH'
				? `Hyde St ${escapeHtml(stop.direction.replace('the ', ''))}`
				: `${escapeHtml(stop.route)} ${escapeHtml(stop.direction)}`;
			const when = mins.length ? mins.map((m) => (m === 0 ? 'now' : m)).join(', ') : '--';
			return `<div class="row"><div class="t">${when}</div><div>${label}</div></div>`;
		};
		const bus = stops.stops.filter((s) => s.agency === 'SF' && s.route !== 'T');
		const rail = stops.stops.filter((s) => s.agency !== 'SF' || s.route === 'T');
		// Caltrain runs far apart: its next three departures as clock times (Oct 26, Jared: "a couple of Caltrain
		// times"), each marked when it isn't a local (511 names the service: "Local Weekday", "Limited", "Express")
		const service = (line = '') => {
			if (/express|bullet/i.test(line)) return ' Exp';
			if (/limited/i.test(line)) return ' Ltd';
			return '';
		};
		const railRow = (stop) => {
			if (stop.agency !== 'CT') return row(stop);
			const next = (byStop[stop.stop] ?? []).filter((a) => new Date(a.t) > now).slice(0, 3);
			const at = (a) => `${time(a.t).replace(/ [AP]M$/, '')}`;
			const [first, ...later] = next;
			return `<div class="row"><div class="t">${first ? at(first) : '--'}</div><div>Caltrain south<span class="dim">${first ? service(first.line) : ''}</span></div></div>
				${later.length ? `<div class="row ct"><div></div><div class="small">then ${later.map((a) => `${at(a)}${service(a.line)}`).join(', ')}</div></div>` : ''}`;
		};
		// BART: only whether an advisory is posted; green OK when it isn't
		const bart = data('bart');
		const advisory = bart?.[0];
		const bartBlock = !bart ? '' : `<div class="hdr gap">BART</div>${advisory
			? `<div class="bart"><span class="tag warn">${escapeHtml(advisory.type ?? 'ALERT')}</span> ${escapeHtml(advisory.text)}</div>`
			: '<div class="bart"><span class="tag ok">OK</span> No advisories</div>'}`;
		return `<div class="cols transit">
			<div><div class="hdr">Next departures &middot; min</div><div class="list">${bus.map(row).join('')}</div></div>
			<div><div class="hdr">Rail</div><div class="list">${rail.map(railRow).join('')}</div>${bartBlock}</div>
		</div>
		<div class="asof">Powered by 511.org &middot; ${asOf(fetchedAt('transit'), now)}</div>`;
	}
}

// GOES-18 GeoColor for the Pacific Southwest, cropped to the Bay Area, as a timelapse of the last three hours; at
// night GeoColor shows low cloud and fog in blue over the city lights. NOAA posts a frame every five minutes (minutes
// ending in 1 and 6, UTC), about five minutes late, named <year><day of year><hhmm>; a frame that isn't there is
// skipped. The loop plays the frames three times over the screen's 18 seconds, holding on the newest.
const GOES = 'https://cdn.star.nesdis.noaa.gov/GOES18/ABI/SECTOR/psw/GEOCOLOR';
const TIMELAPSE_HOURS = 3;
const FRAME_STEP_MINUTES = 15;
const FRAME_MS = 280;
const HOLD_FRAMES = 6;

const goesFrames = (now) => {
	// the newest frame that should be posted: six minutes back, down to a minute ending in 1 or 6
	const latest = new Date(now - 6 * 60000);
	latest.setUTCSeconds(0, 0);
	latest.setUTCMinutes(latest.getUTCMinutes() - ((latest.getUTCMinutes() + 4) % 5));
	const count = (TIMELAPSE_HOURS * 60) / FRAME_STEP_MINUTES;
	return Array.from({ length: count + 1 }, (_, i) => new Date(latest - (count - i) * FRAME_STEP_MINUTES * 60000)).map((t) => {
		const start = Date.UTC(t.getUTCFullYear(), 0, 1);
		const day = String(Math.floor((t - start) / 86400000) + 1).padStart(3, '0');
		const hhmm = `${String(t.getUTCHours()).padStart(2, '0')}${String(t.getUTCMinutes()).padStart(2, '0')}`;
		return { t, url: `${GOES}/${t.getUTCFullYear()}${day}${hhmm}_GOES18-ABI-psw-GEOCOLOR-1200x1200.jpg` };
	});
};

class FogWatch extends SnapshotDisplay {
	constructor(navId, elemId) {
		super(navId, elemId, 'Fog Watch', { seconds: 18 });
		this.animation = null;
	}

	// eslint-disable-next-line class-methods-use-this
	hasData() {
		return true;
	}

	// eslint-disable-next-line class-methods-use-this
	render(now) {
		const line = data('fog')?.lines?.[0];
		return `<div class="fog-frame"><div class="fog-image" style="background-image:url('${GOES}/1200x1200.jpg?_=${Math.floor(now / 600000)}')"></div><div class="fog-pin"></div><div class="fog-time"></div></div>
			<div class="fog-line small">${line ? escapeHtml(line) : 'GOES-18 GeoColor &middot; fog and low cloud show blue after dark'}</div>
			<div class="asof">NOAA/NESDIS STAR &middot; last ${TIMELAPSE_HOURS} hours</div>`;
	}

	async drawCanvas() {
		this.stopAnimation();
		await super.drawCanvas();
		this.startAnimation(new Date());
	}

	// loads the frames (the browser caches them, so later passes only fetch the new ones) and steps through the ones
	// that arrived; until they do, the latest image stands
	startAnimation(now) {
		const image = this.elem.querySelector('.fog-image');
		const stamp = this.elem.querySelector('.fog-time');
		const loaded = [];
		const frames = goesFrames(now);
		frames.forEach((frame, i) => {
			const img = new Image();
			img.onload = () => { loaded[i] = frame; };
			img.src = frame.url;
		});
		let step = 0;
		this.animation = setInterval(() => {
			const ready = loaded.filter(Boolean);
			if (ready.length < 2) return;
			const frame = ready[Math.min(step % (ready.length + HOLD_FRAMES), ready.length - 1)];
			image.style.backgroundImage = `url('${frame.url}')`;
			stamp.textContent = time(frame.t);
			step += 1;
		}, FRAME_MS);
	}

	stopAnimation() {
		clearInterval(this.animation);
		this.animation = null;
	}

	hideCanvas() {
		this.stopAnimation();
		super.hideCanvas();
	}
}

// Air & Fires (was Shakes & Air; decided Oct 26): always in the loop, air as its steady half. The AQI in EPA's colors
// and words, PM2.5, the next 24 hours as a bar strip; fires within 150 miles beside it. Above 150 it's smoke mode
// (the ticker carries a line from 100 up). Quakes moved to their own screen below.
const activeFires = () => (data('fires') ?? []).filter((f) => (f.contained ?? 0) < 90 && (f.acres ?? 0) >= 100);
// EPA's categories and colors (AirNow)
const AQI_BANDS = [
	[50, 'Good', '#00e400'], [100, 'Moderate', '#ffff00'], [150, 'Unhealthy for sensitive groups', '#ff7e00'],
	[200, 'Unhealthy', '#ff0000'], [300, 'Very unhealthy', '#8f3f97'], [Infinity, 'Hazardous', '#7e0023'],
];
const aqiBand = (aqi) => AQI_BANDS.find(([max]) => aqi <= max);
const aqiWord = (aqi) => aqiBand(aqi)[1];
const strip = (values) => {
	const list = (values ?? []).filter((v) => v != null);
	if (!list.length) return '';
	const top = Math.max(100, ...list);
	// labeled as a forecast (Oct 26, Jared: nothing said the bars after now were a prediction)
	return `<div class="aqi-strip-label">Forecast &middot; next 24 hours</div>
		<div class="aqi-strip">${list.map((v) => `<i style="height:${Math.max(6, Math.round((v / top) * 100))}%;background:${aqiBand(v)[2]}"></i>`).join('')}</div>
		<div class="aqi-strip-axis"><span>now</span><span>in 12 h</span><span>in 24 h</span></div>`;
};
class AirFires extends SnapshotDisplay {
	constructor(navId, elemId) {
		super(navId, elemId, 'Air &amp; Fires', { seconds: 18 });
	}

	// eslint-disable-next-line class-methods-use-this
	hasData() {
		return data('air')?.aqi != null || activeFires().length > 0;
	}

	// eslint-disable-next-line class-methods-use-this
	render(now) {
		const air = data('air');
		const fireRows = activeFires().slice(0, 4)
			.map((f) => `<div class="row"><div class="t">${f.contained ?? 0}%</div><div>${escapeHtml(f.name)} &middot; ${Math.round(f.acres).toLocaleString('en-US')} ac</div></div>`);
		const band = air ? aqiBand(air.aqi) : null;
		return `<div class="cols">
			<div>
				<div class="hdr">Air quality${air && air.aqi > 150 ? ' &middot; smoke' : ''}</div>
				${air ? `<div class="aqi-big"><span class="aqi-chip" style="background:${band[2]}"></span><span class="n">${air.aqi}</span> <span class="w">${aqiWord(air.aqi)}</span></div>
				${kv([['PM2.5', `${air.pm25} ug/m3`], ['Forecast peak', `${air.maxNext24}`]])}
				${strip(air.next24)}` : '<div class="dim">No reading</div>'}
			</div>
			<div>
				<div class="hdr">Fires &middot; 150 mi</div>
				<div class="list">${fireRows.join('') || '<div class="dim">No active fires</div>'}</div>
			</div>
		</div>
		<div class="asof">EPA AQI &middot; Open-Meteo &middot; NIFC &middot; ${asOf(fetchedAt('air'), now)}</div>`;
	}
}

// Recent Earthquakes (Oct 26): joins the loop after an M3+ within 100 km (stays 24 h), an M4+ within 241 km (72 h)
// or an M5+ (7 days). A list: magnitude, place, how long ago, distance and direction from Russian Hill, depth, felt
// reports. Research: docs/research-2026-10-26/hazards.md (about 16 qualifying quakes a year)
const HOME = window.RHL.water.point;
const kmFrom = (lat, lon) => {
	const rad = Math.PI / 180;
	const a = Math.sin(((lat - HOME.lat) * rad) / 2) ** 2 + Math.cos(lat * rad) * Math.cos(HOME.lat * rad) * Math.sin(((lon - HOME.lon) * rad) / 2) ** 2;
	return 6371 * 2 * Math.asin(Math.sqrt(a));
};
const bearing = (lat, lon) => {
	const rad = Math.PI / 180;
	const y = Math.sin((lon - HOME.lon) * rad) * Math.cos(lat * rad);
	const x = Math.cos(HOME.lat * rad) * Math.sin(lat * rad) - Math.sin(HOME.lat * rad) * Math.cos(lat * rad) * Math.cos((lon - HOME.lon) * rad);
	const deg = (Math.atan2(y, x) / rad + 360) % 360;
	return ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'][Math.round(deg / 45) % 8];
};
const stays = (q, now) => {
	const km = kmFrom(q.lat, q.lon);
	const age = now - new Date(q.t);
	if (q.mag >= 5) return age < 7 * 24 * HOUR;
	if (q.mag >= 4) return age < 72 * HOUR;
	return q.mag >= 3 && km <= 100 && age < 24 * HOUR;
};
const ago = (t, now) => {
	const minutes = Math.round((now - new Date(t)) / 60000);
	if (minutes < 60) return `${minutes} min ago`;
	if (minutes < 48 * 60) return `${Math.round(minutes / 60)} h ago`;
	return `${weekday(t)} ${time(t)}`;
};
class RecentQuakes extends SnapshotDisplay {
	constructor(navId, elemId) {
		super(navId, elemId, 'Recent Earthquakes', { seconds: 18 });
	}

	// eslint-disable-next-line class-methods-use-this
	hasData() {
		const now = new Date();
		return (data('quakes') ?? []).some((q) => stays(q, now));
	}

	// eslint-disable-next-line class-methods-use-this
	render(now) {
		const all = data('quakes') ?? [];
		const triggers = all.filter((q) => stays(q, now));
		const nearby = all.filter((q) => !triggers.includes(q) && kmFrom(q.lat, q.lon) <= 100 && now - new Date(q.t) < 7 * 24 * HOUR);
		const rows = [...triggers, ...nearby].slice(0, 5).map((q) => {
			const km = kmFrom(q.lat, q.lon);
			const where = `${Math.round(km * 0.621)} mi ${bearing(q.lat, q.lon)}`;
			const felt = q.felt ? ` &middot; ${q.felt.toLocaleString('en-US')} felt it` : '';
			return `<div class="row quake${triggers.includes(q) ? ' trigger' : ''}"><div class="t">M${q.mag.toFixed(1)}</div><div>${escapeHtml(q.place.replace(/, CA$/, ''))}</div></div>
				<div class="row sub"><div></div><div class="dim">${ago(q.t, now)} &middot; ${where} &middot; ${q.depth} km deep${felt}</div></div>`;
		});
		return `<div class="hdr">Within 100 miles &middot; last 7 days</div>
			<div class="list quakes">${rows.join('')}</div>
			<div class="asof">USGS &middot; preliminary &middot; ${asOf(fetchedAt('quakes'), now)}</div>`;
	}
}

// This Day in SF Weather, two pages since Oct 26 (Jared liked it and wanted more): 1) the day's records and normals at
// San Francisco Downtown; 2) rain and the past: how often it rains on the date, the wettest, the rain season since
// Oct 1 against normal, the date last year and a century ago, and the month's all-time extremes. ACIS answers for the
// date it was asked, so a new day waits for it. Page 2's parts are optional; the page goes when none came back.
const MONTH_NAME = (now) => new Intl.DateTimeFormat('en-US', { timeZone: window.RHL.clock.timeZone, month: 'long' }).format(now);
const longDate = (iso) => new Intl.DateTimeFormat('en-US', {
	timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric',
}).format(new Date(`${iso}T12:00:00Z`));
const inches = (value) => `${value.toFixed(2)} in`;
const rainWord = (rain) => {
	if (rain === null) return '';
	return rain >= 0.01 ? `, ${inches(rain)}` : ', dry';
};
const pastDay = (r) => (r ? `${r.hi ?? '--'}&deg;/${r.lo ?? '--'}&deg;${rainWord(r.rain)}` : null);
class ThisDay extends FeatureDisplay {
	constructor(navId, elemId) {
		super(navId, elemId, 'This Day');
	}

	// eslint-disable-next-line class-methods-use-this
	hasData() {
		const day = data('thisDay');
		return !!day && day.date === dayKey(new Date()).slice(5);
	}

	// eslint-disable-next-line class-methods-use-this
	buildPages(now) {
		const day = data('thisDay');
		if (!day) return [];
		const date = new Intl.DateTimeFormat('en-US', { timeZone: window.RHL.clock.timeZone, month: 'long', day: 'numeric' }).format(now);
		const short = new Intl.DateTimeFormat('en-US', { timeZone: window.RHL.clock.timeZone, month: 'short', day: 'numeric' }).format(now);
		const deg = (r) => `${r.value}&deg; (${r.year})`;
		const station = escapeHtml(day.station.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()));
		const pages = [{
			seconds: 12,
			title: ['This Day in', 'SF Weather'],
			html: `<div class="cols">
				<div>
					<div class="hdr">${date} &middot; records</div>
					${kv([['Record high', deg(day.recordHigh)], ['Record low', deg(day.recordLow)], ['Normal', `${day.normalHigh}&deg; / ${day.normalLow}&deg;`]])}
				</div>
				<div>
					<div class="hdr">The other extremes</div>
					${kv([['Coolest high', deg(day.coolestHigh)], ['Warmest low', deg(day.warmestLow)]])}
				</div>
			</div>
			<div class="small dim gap2">Records since 1875 at ${station}</div>
			<div class="asof">NOAA RCC ACIS</div>`,
		}];

		const rainRows = [
			day.rainyYears && ['Rained', `${day.rainyYears.wet} of ${day.rainyYears.of} yrs`],
			['Wettest', `${inches(day.wettest.value)} (${day.wettest.year})`],
		];
		const season = day.season && day.season.inches !== null && day.season.normal
			? [['So far', inches(day.season.inches)], ['Normal by now', inches(day.season.normal)]]
			: null;
		const pastRows = [
			day.lastYear && [`${day.lastYear.year}`, pastDay(day.lastYear)],
			day.centuryAgo && [`${day.centuryAgo.year}`, pastDay(day.centuryAgo)],
		].filter((r) => r && r[1]);
		const monthRows = day.month ? [
			['Hottest', `${day.month.hottest.value}&deg; ${longDate(day.month.hottest.date)}`],
			['Coldest', `${day.month.coldest.value}&deg; ${longDate(day.month.coldest.date)}`],
		] : [];
		if (day.rainyYears || season || pastRows.length || monthRows.length) {
			pages.push({
				seconds: 14,
				title: ['This Day in', 'SF Weather'],
				html: `<div class="cols">
					<div>
						<div class="hdr">Rain on ${short}</div>
						${kv(rainRows)}
						${season ? `<div class="hdr gap">Rain season, since Oct 1</div>${kv(season)}` : ''}
					</div>
					<div>
						${pastRows.length ? `<div class="hdr">${short}, years ago</div>${kv(pastRows)}` : ''}
						${monthRows.length ? `<div class="hdr${pastRows.length ? ' gap' : ''}">${MONTH_NAME(now)}, all time</div>${kv(monthRows)}` : ''}
					</div>
				</div>
				<div class="asof">NOAA RCC ACIS &middot; ${station}</div>`,
			});
		}
		return pages;
	}
}

registerDisplay(new GettingAround(17, 'getting-around'));
registerDisplay(new FogWatch(18, 'fog-watch'));
registerDisplay(new AirFires(19, 'shakes-air'));
const thisDay = new ThisDay(20, 'this-day');
thisDay.makeLoopScreen();
registerDisplay(thisDay);
registerDisplay(new RecentQuakes(30, 'recent-quakes'));
